import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle, MagnifyingGlass, Trash, Package } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// VERSION 7.0 - Quantity input + ADD button for each search result
const FORM_VERSION = 'v7.0';
const STORAGE_KEY = 'rfq_parts_v7';

const saveParts = (parts) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(parts)); } catch(e) {}
};
const loadParts = () => {
  try { 
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : [];
  } catch(e) { return []; }
};
const clearParts = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch(e) {}
};

console.log(`=== RFQFormPage ${FORM_VERSION} LOADED ===`);

const RFQFormPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '', company_name: '', job_title: '', email: '', phone: '',
    preferred_contact: 'Email',
    hq_address: { street: '', city: '', state: '', zip: '', country: 'USA' },
    cpu: '', server: '',
    quantity: '', min_quantity: '', allow_partial: false, repeat_demand: 'One-time',
    target_price: '', currency: 'USD', flexible_price: false,
    date_needed: '', urgency: 'Standard',
    shipping_method: 'Ground', shipping_terms: 'FOB',
    payment_method: 'Credit Card', request_net_terms: false,
    additional_notes: ''
  });
  
  const [selectedParts, setSelectedParts] = useState(() => loadParts());
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [partSearch, setPartSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Track quantity for each search result (by part name)
  const [searchQtys, setSearchQtys] = useState({});

  useEffect(() => { saveParts(selectedParts); }, [selectedParts]);
  useEffect(() => { if (submitted) clearParts(); }, [submitted]);

  // Search
  const searchParts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      const res = await axios.get(`${API_URL}/api/inventory?search=${encodeURIComponent(query)}&limit=10`);
      const items = res.data.items || [];
      setSearchResults(items);
      // Don't pre-fill quantities - let user type fresh
      setSearchQtys({});
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchParts(partSearch), 300);
    return () => clearTimeout(t);
  }, [partSearch, searchParts]);

  // Update qty for a search result
  const updateSearchQty = (partName, qty) => {
    setSearchQtys(prev => ({ ...prev, [partName]: parseInt(qty) || 1 }));
  };

  // ADD button handler
  const handleAdd = (part) => {
    const qty = searchQtys[part.name] || 1;
    
    if (selectedParts.find(p => p.name === part.name)) {
      alert('This part is already in your RFQ!');
      return;
    }

    const newPart = {
      name: part.name,
      description: part.description,
      condition: part.condition,
      taa: part.taa,
      qty_on_hand: part.qty_on_hand,
      requested_qty: qty,
      available_qty_at_selection: part.qty_on_hand,
      selected_at: new Date().toISOString()
    };

    const newList = [...selectedParts, newPart];
    setSelectedParts(newList);
    saveParts(newList);
    
    alert(`ADDED TO RFQ!\n\nPart: ${part.name}\nQuantity: ${qty}\n\nTotal parts in RFQ: ${newList.length}`);
    toast.success(`Added ${part.name} x${qty}`);
  };

  // Remove part from list
  const handleRemove = (index) => {
    const updated = selectedParts.filter((_, i) => i !== index);
    setSelectedParts(updated);
    saveParts(updated);
  };

  // Update qty in selected parts
  const handleUpdateSelectedQty = (index, qty) => {
    const updated = selectedParts.map((p, i) => 
      i === index ? { ...p, requested_qty: parseInt(qty) || 1 } : p
    );
    setSelectedParts(updated);
    saveParts(updated);
  };

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return { ...prev, [parent]: { ...prev[parent], [child]: value } };
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const handleFileUpload = useCallback((e) => {
    Array.from(e.target.files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setUploadedFiles(prev => [...prev, {
          id: Date.now(), filename: file.name, size: file.size, type: file.type, data: ev.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const handleSubmit = async () => {
    setErrorMessage('');
    if (!formData.full_name || !formData.company_name || !formData.email || !formData.phone) {
      alert('Please fill: Full Name, Company, Email, Phone');
      return;
    }
    
    const parts = selectedParts.length > 0 ? selectedParts : loadParts();
    
    try {
      setSubmitting(true);
      const totalQty = parts.reduce((sum, p) => sum + (p.requested_qty || 0), 0);
      
      const submission = {
        ...formData,
        quantity: totalQty || parseInt(formData.quantity) || null,
        min_quantity: formData.min_quantity ? parseInt(formData.min_quantity) : null,
        uploaded_files: uploadedFiles,
        selected_parts: parts.map(p => ({
          part_number: p.name,
          description: p.description,
          condition: p.condition,
          taa: p.taa,
          requested_qty: p.requested_qty,
          available_qty_at_selection: p.available_qty_at_selection
        }))
      };
      
      await axios.post(`${API_URL}/api/rfq/submit`, submission, { timeout: 15000 });
      clearParts();
      setSubmitted(true);
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Failed');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <CheckCircle size={80} className="mx-auto text-[#10B981] mb-6" weight="fill" />
          <h1 className="text-4xl font-mono font-bold mb-4">RFQ Submitted!</h1>
          <Button onClick={() => window.location.href = '/'} className="bg-[#0047AB]">Home</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const totalQty = selectedParts.reduce((sum, p) => sum + (p.requested_qty || 0), 0);
  const steps = [
    { num: 1, title: 'Contact' },
    { num: 2, title: 'Parts' },
    { num: 3, title: 'Details' },
    { num: 4, title: 'Review' }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Parts counter banner */}
        <div className="mb-4 p-4 bg-[#F59E0B] rounded-lg text-center">
          <span className="text-black font-mono font-bold text-xl">
            {FORM_VERSION} | RFQ PARTS: {selectedParts.length} | TOTAL QTY: {totalQty}
          </span>
        </div>

        <h1 className="text-3xl font-mono font-bold mb-6">Request For Quote</h1>

        {/* Steps */}
        <div className="flex justify-between mb-6">
          {steps.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div className="flex flex-col items-center cursor-pointer" onClick={() => setCurrentStep(step.num)}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                  currentStep >= step.num ? 'bg-[#0047AB] text-white' : 'bg-[#1E293B] text-[#94A3B8]'
                }`}>
                  {step.num}
                </div>
                <span className={`mt-1 text-sm ${currentStep >= step.num ? 'text-white' : 'text-[#94A3B8]'}`}>
                  {step.title}
                </span>
              </div>
              {idx < 3 && <div className={`flex-1 h-1 mx-2 mt-6 ${currentStep > step.num ? 'bg-[#0047AB]' : 'bg-[#1E293B]'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6">
          
          {/* Show selected parts on all steps if any */}
          {selectedParts.length > 0 && (
            <div className="mb-6 p-4 bg-[#10B981]/20 border-2 border-[#10B981] rounded-lg">
              <p className="font-mono text-[#10B981] font-bold text-lg">YOUR RFQ ({selectedParts.length} parts)</p>
              {selectedParts.map((p, i) => (
                <p key={i} className="text-white text-sm">• {p.name} - Qty: {p.requested_qty}</p>
              ))}
            </div>
          )}

          {/* STEP 1: Contact */}
          <div className={currentStep === 1 ? 'block' : 'hidden'}>
            <h2 className="text-xl font-mono text-[#007AFF] mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Full Name *</Label><Input value={formData.full_name} onChange={(e) => updateFormData('full_name', e.target.value)} className="bg-transparent border-[#334155]" /></div>
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => updateFormData('company_name', e.target.value)} className="bg-transparent border-[#334155]" /></div>
              <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => updateFormData('email', e.target.value)} className="bg-transparent border-[#334155]" /></div>
              <div><Label>Phone *</Label><Input value={formData.phone} onChange={(e) => updateFormData('phone', e.target.value)} className="bg-transparent border-[#334155]" /></div>
            </div>
            <div className="mt-4">
              <Label>Address</Label>
              <Input value={formData.hq_address.street} onChange={(e) => updateFormData('hq_address.street', e.target.value)} className="bg-transparent border-[#334155] mb-2" placeholder="Street" />
              <div className="grid grid-cols-4 gap-2">
                <Input value={formData.hq_address.city} onChange={(e) => updateFormData('hq_address.city', e.target.value)} className="bg-transparent border-[#334155]" placeholder="City" />
                <Input value={formData.hq_address.state} onChange={(e) => updateFormData('hq_address.state', e.target.value)} className="bg-transparent border-[#334155]" placeholder="State" />
                <Input value={formData.hq_address.zip} onChange={(e) => updateFormData('hq_address.zip', e.target.value)} className="bg-transparent border-[#334155]" placeholder="ZIP" />
                <Input value={formData.hq_address.country} onChange={(e) => updateFormData('hq_address.country', e.target.value)} className="bg-transparent border-[#334155]" placeholder="Country" />
              </div>
            </div>
          </div>

          {/* STEP 2: Parts - WITH QTY INPUT + ADD BUTTON ON EACH RESULT */}
          <div className={currentStep === 2 ? 'block' : 'hidden'}>
            <h2 className="text-xl font-mono text-[#007AFF] mb-4">Select Parts from Inventory</h2>
            
            {/* Search */}
            <div className="mb-4">
              <Label className="text-lg">Search Part Number</Label>
              <div className="relative mt-2">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input
                  value={partSearch}
                  onChange={(e) => setPartSearch(e.target.value)}
                  className="bg-transparent border-[#334155] pl-10 h-14 text-lg"
                  placeholder="Type part number (e.g. HMA81)"
                />
              </div>
            </div>

            {searchLoading && <p className="text-[#94A3B8] py-4">Searching...</p>}
            
            {partSearch.length >= 2 && !searchLoading && searchResults.length === 0 && (
              <p className="text-[#94A3B8] py-4">No parts found</p>
            )}

            {/* Search Results - Each with QTY input and ADD button */}
            {searchResults.length > 0 && (
              <div className="space-y-4 mb-6">
                <p className="text-[#F59E0B] font-mono font-bold">SEARCH RESULTS ({searchResults.length})</p>
                {searchResults.map((part, idx) => (
                  <div key={idx} className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
                    {/* Part info */}
                    <div className="mb-3">
                      <p className="font-mono text-[#007AFF] font-bold text-lg">{part.name}</p>
                      <p className="text-sm text-[#94A3B8]">{part.description}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">
                        Available: <span className="text-[#10B981] font-bold">{part.qty_on_hand}</span> | 
                        Condition: {part.condition}
                      </p>
                    </div>
                    
                    {/* QTY INPUT + ADD BUTTON ROW */}
                    <div className="flex items-center gap-3 pt-3 border-t border-[#334155]">
                      <Label className="text-white font-bold whitespace-nowrap">QTY:</Label>
                      <Input
                        type="number"
                        min="1"
                        value={searchQtys[part.name] || ''}
                        onChange={(e) => updateSearchQty(part.name, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder="1"
                        className="bg-[#05050A] border-[#10B981] w-20 h-12 text-center text-xl font-bold placeholder:text-[#666]"
                      />
                      <Button
                        type="button"
                        onClick={() => handleAdd(part)}
                        className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-8 py-3 h-12 text-lg flex-1"
                      >
                        + ADD TO RFQ
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Already Selected Parts */}
            <div className="mt-6 pt-4 border-t border-[#334155]">
              <p className="text-[#10B981] font-mono font-bold text-lg mb-3">
                YOUR RFQ LIST ({selectedParts.length} parts)
              </p>
              
              {selectedParts.length === 0 ? (
                <div className="bg-[#1E293B]/30 border-2 border-dashed border-[#334155] rounded-lg p-6 text-center">
                  <Package size={40} className="mx-auto text-[#94A3B8] mb-2" />
                  <p className="text-[#94A3B8]">No parts added yet</p>
                  <p className="text-[#94A3B8] text-sm">Search above, enter quantity, and click ADD</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedParts.map((part, idx) => (
                    <div key={idx} className="bg-[#10B981]/10 border-2 border-[#10B981] rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-mono text-white font-bold">{part.name}</p>
                          <p className="text-xs text-[#94A3B8]">{part.description}</p>
                        </div>
                        <Button
                          type="button"
                          onClick={() => handleRemove(idx)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 h-auto"
                        >
                          <Trash size={16} className="mr-1" /> Remove
                        </Button>
                      </div>
                      <div className="flex items-center gap-3">
                        <Label className="text-white">Qty:</Label>
                        <Input
                          type="number"
                          min="1"
                          value={part.requested_qty}
                          onChange={(e) => handleUpdateSelectedQty(idx, e.target.value)}
                          className="bg-[#05050A] border-[#10B981] w-20 h-10 text-center font-bold"
                        />
                        <span className="text-[#94A3B8] text-sm">(Avail: {part.available_qty_at_selection})</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Server/CPU */}
            <div className="mt-6 pt-4 border-t border-[#334155]">
              <p className="text-[#94A3B8] mb-2">Optional: System info</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>CPU</Label><Input value={formData.cpu} onChange={(e) => updateFormData('cpu', e.target.value)} className="bg-transparent border-[#334155]" placeholder="Intel Xeon" /></div>
                <div><Label>Server</Label><Input value={formData.server} onChange={(e) => updateFormData('server', e.target.value)} className="bg-transparent border-[#334155]" placeholder="Dell R740" /></div>
              </div>
            </div>
          </div>

          {/* STEP 3: Details (Timing, Payment, Files) */}
          <div className={currentStep === 3 ? 'block' : 'hidden'}>
            <h2 className="text-xl font-mono text-[#007AFF] mb-4">Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Target Price</Label><Input value={formData.target_price} onChange={(e) => updateFormData('target_price', e.target.value)} className="bg-transparent border-[#334155]" placeholder="$500" /></div>
                <div>
                  <Label>Repeat Demand</Label>
                  <Select value={formData.repeat_demand} onValueChange={(v) => updateFormData('repeat_demand', v)}>
                    <SelectTrigger className="bg-transparent border-[#334155]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="One-time">One-time</SelectItem>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Date Needed</Label><Input type="date" value={formData.date_needed} onChange={(e) => updateFormData('date_needed', e.target.value)} className="bg-transparent border-[#334155]" /></div>
                <div>
                  <Label>Urgency</Label>
                  <Select value={formData.urgency} onValueChange={(v) => updateFormData('urgency', v)}>
                    <SelectTrigger className="bg-transparent border-[#334155]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Urgent">Urgent</SelectItem>
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="Flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.allow_partial} onCheckedChange={(c) => updateFormData('allow_partial', c)} />
                <Label>Allow Partial Shipment</Label>
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={formData.payment_method} onValueChange={(v) => updateFormData('payment_method', v)}>
                  <SelectTrigger className="bg-transparent border-[#334155]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Credit Card">Credit Card</SelectItem>
                    <SelectItem value="Purchase Order">Purchase Order</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Upload Files (PO, Images)</Label>
                <input type="file" multiple accept="image/*,.pdf" onChange={handleFileUpload} className="block w-full text-sm text-[#94A3B8] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#0047AB] file:text-white mt-2" />
                {uploadedFiles.map((f, i) => (
                  <p key={i} className="text-sm text-[#10B981] mt-1"><CheckCircle size={14} className="inline mr-1" />{f.filename}</p>
                ))}
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={formData.additional_notes} onChange={(e) => updateFormData('additional_notes', e.target.value)} className="bg-transparent border-[#334155]" rows={3} />
              </div>
            </div>
          </div>

          {/* STEP 4: Review */}
          <div className={currentStep === 4 ? 'block' : 'hidden'}>
            <h2 className="text-xl font-mono text-[#007AFF] mb-4">Review & Submit</h2>
            
            <div className="bg-[#F59E0B]/20 border border-[#F59E0B] rounded p-3 mb-4">
              <p className="font-mono text-[#F59E0B] font-bold">{FORM_VERSION} | PARTS: {selectedParts.length} | FILES: {uploadedFiles.length}</p>
            </div>

            <div className="space-y-4">
              <div className="bg-[#1E293B]/30 p-4 rounded">
                <h4 className="font-mono text-[#007AFF]">Contact</h4>
                <p className="text-white">{formData.full_name} - {formData.company_name}</p>
                <p className="text-[#94A3B8]">{formData.email} | {formData.phone}</p>
              </div>

              <div className="bg-[#1E293B]/30 p-4 rounded">
                <h4 className="font-mono text-[#007AFF]">Parts ({selectedParts.length})</h4>
                {selectedParts.length > 0 ? (
                  selectedParts.map((p, i) => (
                    <div key={i} className="bg-[#05050A]/50 p-2 rounded mt-2">
                      <p className="font-mono text-white">{p.name}</p>
                      <p className="text-xs text-[#94A3B8]">{p.description}</p>
                      <p className="text-sm text-[#10B981]">Qty: {p.requested_qty}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-red-500 font-bold">NO PARTS - Go back to Step 2!</p>
                )}
              </div>

              <div className="bg-[#1E293B]/30 p-4 rounded">
                <h4 className="font-mono text-[#007AFF]">Details</h4>
                <p>Date: {formData.date_needed || 'Flexible'} | Urgency: {formData.urgency}</p>
                {formData.target_price && <p>Target: {formData.target_price}</p>}
                <p>Payment: {formData.payment_method}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-[#1E293B]">
            <Button onClick={() => setCurrentStep(s => Math.max(1, s - 1))} disabled={currentStep === 1} variant="outline" className="border-[#334155]">
              <ArrowLeft className="mr-2" size={20} /> Back
            </Button>
            {currentStep < 4 ? (
              <Button onClick={() => setCurrentStep(s => s + 1)} className="bg-[#0047AB] hover:bg-[#005DF0]">
                Next <ArrowRight className="ml-2" size={20} />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#10B981] hover:bg-[#059669]">
                {submitting ? 'Submitting...' : 'Submit RFQ'} <CheckCircle className="ml-2" size={20} />
              </Button>
            )}
          </div>

          {errorMessage && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500 rounded">
              <p className="text-red-500 text-center">{errorMessage}</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RFQFormPage;
